import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import { toggleBold, toggleItalic, toggleUnderline } from '../../../utils/richTextCommands';
import './SignatureEditor.css';

/**
 * SignatureEditor — Outlook-style Rx signature editor for a DOCTOR staff
 * member. Only ever rendered by StaffFormRow when editing an EXISTING
 * doctor. Text only — an uploaded signature IMAGE was deliberately removed
 * (not confirmed legally equivalent to a real digital signature under
 * India's IT Act, 2000).
 *
 * Text changes only lift up to the parent form via onTextChange — the
 * actual PUT .../signature call happens once, when the admin saves the
 * whole staff edit form (see ManageStaffModal's saveEdit), not on every
 * keystroke.
 *
 * Props:
 *   loading             — true while the parent's GET .../signature hydration
 *                         fetch is in flight; renders a placeholder instead
 *                         of mounting the editor
 *   initialSignatureHtml — the fetched signature_html
 *   onTextChange        — (html) => void, called on every edit
 *   fontFamily          — 'sans' | 'serif' | 'mono' | '' — uniform styling for
 *                         the WHOLE signature block, a plain controlled select
 *                         (not applied to a text selection via execCommand,
 *                         unlike Bold/Italic/Underline above)
 *   onFontFamilyChange  — (value) => void
 *   fontSize            — number | '' — points, 8-16
 *   onFontSizeChange    — (value) => void
 *   disabled            — boolean
 */
const SignatureEditor = ({
  loading = false,
  initialSignatureHtml = '',
  onTextChange,
  fontFamily = '',
  onFontFamilyChange,
  fontSize = '',
  onFontSizeChange,
  disabled = false,
}) => {
  const { theme } = useTheme();
  const editableRef = useRef(null);
  const hydratedRef = useRef(false);

  // Hydrate the contentEditable exactly once, the first time `loading`
  // becomes false. NOTE: this component does NOT unmount/remount across the
  // loading→loaded transition — the `if (loading) return ...` below is an
  // early return inside THIS component, not a different component the
  // parent swaps in, so a plain mount-only `useEffect(fn, [])` fires while
  // still loading (editableRef.current is null then, since the contentEditable
  // div isn't rendered yet) and never fires again once the real div appears —
  // that was a real bug: the fetched signature never reached the DOM. The
  // `hydratedRef` guard (rather than depending only on `loading`) also stops
  // this from re-running on every keystroke, since `initialSignatureHtml` is
  // itself derived from the parent's live edit state.
  useEffect(() => {
    if (!loading && !hydratedRef.current && editableRef.current) {
      editableRef.current.innerHTML = initialSignatureHtml || '';
      hydratedRef.current = true;
    }
  }, [loading, initialSignatureHtml]);

  const emitTextChange = () => {
    if (editableRef.current) {
      onTextChange(editableRef.current.innerHTML);
    }
  };

  const applyFormat = (fn) => {
    editableRef.current?.focus();
    fn();
    emitTextChange();
  };

  const btnStyle = {
    backgroundColor: theme.background.surface,
    borderColor: theme.brand.purple,
    color: theme.brand.purple,
  };

  if (loading) {
    return (
      <div className="signature-editor-loading" style={{ color: theme.text.secondary }}>
        Loading signature...
      </div>
    );
  }

  return (
    <div className="signature-editor" style={{ borderColor: theme.border.default }}>
      <p className="signature-editor-hint" style={{ color: theme.text.secondary }}>
        Your custom signature replaces the automatically printed doctor name and specialization on prescriptions.
      </p>

      <div className="signature-editor-toolbar">
        <button
          type="button"
          className="signature-editor-format-btn"
          onClick={() => applyFormat(toggleBold)}
          disabled={disabled}
          title="Bold"
          style={btnStyle}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className="signature-editor-format-btn"
          onClick={() => applyFormat(toggleItalic)}
          disabled={disabled}
          title="Italic"
          style={btnStyle}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className="signature-editor-format-btn"
          onClick={() => applyFormat(toggleUnderline)}
          disabled={disabled}
          title="Underline"
          style={btnStyle}
        >
          <u>U</u>
        </button>

        <select
          className="signature-editor-font-select"
          value={fontFamily || ''}
          onChange={(e) => onFontFamilyChange(e.target.value || null)}
          disabled={disabled}
          aria-label="Signature font family"
          style={btnStyle}
        >
          <option value="">Default</option>
          <option value="sans">Sans</option>
          <option value="serif">Serif</option>
          <option value="mono">Monospace</option>
        </select>

        <select
          className="signature-editor-font-select"
          value={fontSize || ''}
          onChange={(e) => onFontSizeChange(e.target.value ? Number(e.target.value) : null)}
          disabled={disabled}
          aria-label="Signature font size"
          style={btnStyle}
        >
          <option value="">Default</option>
          {Array.from({ length: 9 }, (_, i) => i + 8).map((size) => (
            <option key={size} value={size}>{size}pt</option>
          ))}
        </select>
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="signature-editor-text"
        style={{
          backgroundColor: theme.background.surface,
          color: theme.text.primary,
          borderColor: theme.border.input,
          fontFamily: fontFamily === 'serif'
            ? "Georgia, 'Times New Roman', serif"
            : fontFamily === 'mono'
              ? "'Courier New', Courier, monospace"
              : undefined,
          fontSize: fontSize ? `${fontSize}pt` : undefined,
        }}
        contentEditable={!disabled}
        ref={editableRef}
        onInput={emitTextChange}
        onBlur={emitTextChange}
        role="textbox"
        aria-multiline="true"
        aria-label="Signature text"
        suppressContentEditableWarning
      />
    </div>
  );
};

export default SignatureEditor;
